import type { ShapeLayer } from './template';

/**
 * Outlines of the decorative shapes as SVG path data in a unit box (0..1 on both axes), so they
 * stretch with the layer like rectangles and ellipses do. The renderer clips a CSS box with the
 * path (fills and gradients behave exactly as on rectangles) and strokes it on top.
 */

const r = (n: number) => Math.round(n * 10000) / 10000;

function polygonPath(pts: Array<[number, number]>): string {
  // Fit the points to the whole box: a pentagon or a star should fill the layer, not float in it
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const sx = x1 - x0 || 1;
  const sy = y1 - y0 || 1;
  return (
    pts.map(([x, y], i) => `${i ? 'L' : 'M'}${r((x - x0) / sx)} ${r((y - y0) / sy)}`).join(' ') +
    ' Z'
  );
}

/** Regular polygon or star with the first vertex at the top. */
function radial(count: number, inner: number | null): string {
  const n = Math.max(3, Math.min(64, Math.round(count)));
  const pts: Array<[number, number]> = [];
  const steps = inner === null ? n : n * 2;
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const rad = inner !== null && i % 2 ? inner : 1;
    pts.push([0.5 + 0.5 * rad * Math.sin(a), 0.5 - 0.5 * rad * Math.cos(a)]);
  }
  return polygonPath(pts);
}

export const DEFAULT_POLYGON_SIDES = 6;
export const DEFAULT_STAR_POINTS = 5;
export const DEFAULT_STAR_INNER = 0.45;

const STATIC_PATHS: Partial<Record<ShapeLayer['shape'], string>> = {
  triangle: 'M0.5 0 L1 1 L0 1 Z',
  heart:
    'M0.5 0.22 C0.5 0.08 0.36 0 0.24 0 C0.1 0 0 0.12 0 0.29 C0 0.56 0.3 0.74 0.5 1 C0.7 0.74 1 0.56 1 0.29 C1 0.12 0.9 0 0.76 0 C0.64 0 0.5 0.08 0.5 0.22 Z',
  arrow: 'M0 0.3 L0.6 0.3 L0.6 0 L1 0.5 L0.6 1 L0.6 0.7 L0 0.7 Z',
  chevron: 'M0 0 L0.7 0 L1 0.5 L0.7 1 L0 1 L0.3 0.5 Z',
  cross:
    'M0.34 0 L0.66 0 L0.66 0.34 L1 0.34 L1 0.66 L0.66 0.66 L0.66 1 L0.34 1 L0.34 0.66 L0 0.66 L0 0.34 L0.34 0.34 Z',
  bubble:
    'M0.1 0 L0.9 0 Q1 0 1 0.12 L1 0.66 Q1 0.78 0.9 0.78 L0.4 0.78 L0.16 1 L0.2 0.78 L0.1 0.78 Q0 0.78 0 0.66 L0 0.12 Q0 0 0.1 0 Z',
  ribbon: 'M0 0 L1 0 L0.92 0.5 L1 1 L0 1 L0.08 0.5 Z',
  tag: 'M0 0.5 L0.22 0 L1 0 L1 1 L0.22 1 Z M0.13 0.5 A0.045 0.07 0 1 0 0.22 0.5 A0.045 0.07 0 1 0 0.13 0.5 Z',
  parallelogram: 'M0.22 0 L1 0 L0.78 1 L0 1 Z',
  semicircle: 'M0 1 A0.5 1 0 0 1 1 1 Z',
  blob: 'M0.55 0.02 C0.78 0.03 0.97 0.18 0.98 0.42 C0.99 0.66 0.86 0.9 0.6 0.97 C0.36 1 0.1 0.9 0.03 0.65 C0 0.42 0.08 0.22 0.25 0.1 C0.34 0.04 0.44 0.01 0.55 0.02 Z',
};

/** Shapes drawn from an outline (everything except rectangles, ellipses, lines and icons). */
export function isOutlineShape(shape: ShapeLayer['shape']): boolean {
  return !['rectangle', 'ellipse', 'line', 'icon'].includes(shape);
}

/** Path data of the shape in the unit box, or null when it is drawn with plain CSS. */
export function shapeOutline(
  layer: Pick<ShapeLayer, 'shape'> & { points?: number; innerRatio?: number },
): string | null {
  switch (layer.shape) {
    case 'polygon':
      return radial(layer.points ?? DEFAULT_POLYGON_SIDES, null);
    case 'star':
      return radial(
        layer.points ?? DEFAULT_STAR_POINTS,
        Math.min(0.98, Math.max(0.05, layer.innerRatio ?? DEFAULT_STAR_INNER)),
      );
    default:
      return STATIC_PATHS[layer.shape] ?? null;
  }
}
